// Workspace (F-18, D-41): the projects open on this computer, each with several shots, shown as the tree on the
// left. The active shot's state is the store's project; the others are kept here. The workspace is kept in
// IndexedDB and restored on the next launch (D-28). Videos never enter the project file: each shot remembers file
// handles (same computer) and file identity (name, size, fingerprint) to re-link them, also from a media folder.

import type { App } from './app';
import { APP_VERSION } from './app';
import type { Project, Source, SourceId } from '../core/types';
import type { MediaInput, SequenceInput } from '../media/source-client';
import { groupSequences, isSequenceImage } from '../core/sequence';
import { sequenceFromDirectory, sequenceFromGroup, sequenceIdentity } from '../media/sequence-input';
import { createProject, fingerprintFile, newId, PROJECT_EXT } from '../core/project';
import { createDoc, docContentKey, isVideoName, parseDoc, rankCandidates, serializeDoc, type ProjectDoc } from '../core/project-doc';
import { ensureWritable, fsAccess, kvGet, kvSet, writeFile, type AutosaveRecord } from './persist';
import { t } from '../i18n';
import { downloadBlob, toast } from '../ui/dom';
import { downloadRemote } from '../media/remote-download';
import { saveIntoFolder } from './app';

/** Per slot: the video's file handle, or the folder of an image sequence (F-01.12). */
type Handles = Partial<Record<SourceId, FileSystemHandle>>;

export interface WsShot {
  id: string;
  project: Project;
  handles: Handles;
}

export interface WsProject {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  extra?: Record<string, unknown>;
  shots: WsShot[];
  activeShotId?: string;
  /** The .acproj file this project was opened from or saved to. */
  fileHandle?: FileSystemFileHandle;
  fileName?: string;
  /** Content key at the last open/save; null = never saved. */
  baseline: string | null;
  readOnly: boolean;
  expanded: boolean;
  /** Media folder chosen for automatic matching (one permission click re-opens every shot's videos). */
  mediaDir?: FileSystemDirectoryHandle;
}

export type ShotStatus = 'linked' | 'relink' | 'empty' | 'loading';

interface WsRecord {
  v: 1;
  active: { pid: string; sid: string } | null;
  projects: {
    json: string;
    fileHandle?: FileSystemFileHandle;
    fileName?: string;
    baseline: string | null;
    readOnly: boolean;
    expanded: boolean;
    mediaDir?: FileSystemDirectoryHandle;
    handles: Record<string, Handles>;
  }[];
}

type PermHandle = FileSystemHandle & {
  queryPermission?: (o: { mode: 'read' | 'readwrite' }) => Promise<PermissionState>;
  requestPermission?: (o: { mode: 'read' | 'readwrite' }) => Promise<PermissionState>;
};

/** Read access to a stored handle; asks the user only when `ask` (must run inside a click). */
async function readable(h: FileSystemHandle, ask: boolean): Promise<boolean> {
  const ph = h as PermHandle;
  try {
    let p = (await ph.queryPermission?.({ mode: 'read' })) ?? 'granted';
    if (p !== 'granted' && ask) p = (await ph.requestPermission?.({ mode: 'read' })) ?? 'denied';
    return p === 'granted';
  } catch {
    return false;
  }
}

interface FoundMedia {
  handle: FileSystemFileHandle;
  file: File;
  name: string;
  size: number;
}

/** An image sequence found in a media folder (matched by pattern and frame count, then fingerprint). */
interface FoundSequence {
  dir: FileSystemDirectoryHandle;
  seq: SequenceInput;
}

type IterableDir = FileSystemDirectoryHandle & { values(): AsyncIterable<FileSystemHandle> };

/**
 * Video files and image sequences under a folder (a few levels deep, bounded so a huge NAS share cannot stall the
 * app). Image files are grouped per folder into sequences.
 */
async function scanFolder(dir: FileSystemDirectoryHandle, depth = 3, limit = 50000): Promise<{ videos: FoundMedia[]; sequences: FoundSequence[] }> {
  const videos: FoundMedia[] = [];
  const sequences: FoundSequence[] = [];
  let seen = 0;
  const walk = async (d: FileSystemDirectoryHandle, level: number): Promise<void> => {
    const images: { name: string; file: File }[] = [];
    for await (const h of (d as IterableDir).values()) {
      if (++seen > limit) break;
      if (h.kind === 'file' && (isVideoName(h.name) || isSequenceImage(h.name))) {
        try {
          const file = await (h as FileSystemFileHandle).getFile();
          if (isSequenceImage(h.name)) images.push({ name: file.name, file });
          else videos.push({ handle: h as FileSystemFileHandle, file, name: file.name, size: file.size });
        } catch {
          /* unreadable entry */
        }
      } else if (h.kind === 'directory' && level < depth && !h.name.startsWith('.')) {
        await walk(h as FileSystemDirectoryHandle, level + 1);
      }
    }
    for (const g of groupSequences(images)) sequences.push({ dir: d, seq: sequenceFromGroup(g, 24) });
  };
  await walk(dir, 0);
  return { videos, sequences };
}

/** Sequence settings of a source carried into the worker input (fps and EXR view are the user's). */
function withSourceSettings(seq: SequenceInput, s: Source): SequenceInput {
  return { ...seq, fps: s.spec.frameRate, exrView: s.sequence?.exrView };
}

export class Workspace {
  projects: WsProject[] = [];
  active: { pid: string; sid: string } | null = null;
  /** Files already opened this session, per shot: switching back re-opens them without asking. */
  private files = new Map<string, Map<SourceId, { media: MediaInput; handle?: FileSystemHandle }>>();
  private dirty = new Map<string, boolean>();
  private activationSeq = 0;
  /** Linked videos being downloaded again (F-01.13), by remote id; the relink card says so. */
  readonly fetching = new Set<string>();
  private dirtyTimer = 0;
  private persistTimer = 0;
  /** True while a media folder is scanned (the relink card shows progress). */
  scanning = false;

  constructor(private app: App) {}

  // ---------------------------------------------------------------------------------------- queries

  project(pid: string): WsProject | undefined {
    return this.projects.find((p) => p.id === pid);
  }

  activeProject(): WsProject | undefined {
    return this.active ? this.project(this.active.pid) : undefined;
  }

  activeShot(): WsShot | undefined {
    return this.activeProject()?.shots.find((s) => s.id === this.active!.sid);
  }

  /** The live project of a shot (the store's for the active shot). */
  shotProject(sh: WsShot): Project {
    return this.active?.sid === sh.id ? this.app.store.state.project : sh.project;
  }

  shotLabel(wp: WsProject, sh: WsShot): string {
    return this.shotProject(sh).name || t('tree.untitledShot', { n: wp.shots.indexOf(sh) + 1 });
  }

  isDirty(pid: string): boolean {
    const wp = this.project(pid);
    if (!wp) return false;
    if (wp.baseline === null) return true;
    return this.dirty.get(pid) ?? false;
  }

  anyUnsavedWork(): boolean {
    return this.projects.some((p) => (p.baseline === null ? this.hasContent(p) : this.isDirty(p.id)));
  }

  /** A never-saved project with nothing in it (the default one) is not worth a warning. */
  private hasContent(wp: WsProject): boolean {
    return wp.shots.length > 1 || wp.shots.some((s) => {
      const p = this.shotProject(s);
      return p.sources.length > 0;
    });
  }

  shotStatus(sh: WsShot): ShotStatus {
    const p = this.shotProject(sh);
    if (!p.sources.length) return 'empty';
    if (this.active?.sid === sh.id) {
      const s = this.app.sources;
      if (p.sources.some((x) => s.loading.has(x.id))) return 'loading';
      return p.sources.every((x) => s.runtime.has(x.id)) ? 'linked' : 'relink';
    }
    const mem = this.files.get(sh.id);
    return p.sources.every((x) => mem?.has(x.id) || sh.handles[x.id]) ? 'linked' : 'relink';
  }

  /** Missing videos of the active shot that have a stored handle (one click re-opens them). */
  hasStoredHandles(): boolean {
    const sh = this.activeShot();
    if (!sh) return false;
    const s = this.app.sources;
    return this.app.project.sources.some((x) => !s.runtime.has(x.id) && !s.loading.has(x.id) && !!sh.handles[x.id]);
  }

  // ---------------------------------------------------------------------------------------- state sync

  /** Copies the live state of the active shot (project object, opened files) into the workspace. */
  sync(): void {
    const sh = this.activeShot();
    if (!sh) return;
    const p = this.app.store.state.project;
    sh.project = p;
    const ids = new Set(p.sources.map((s) => s.id));
    const mem = this.files.get(sh.id) ?? new Map();
    for (const [id, rt] of this.app.sources.runtime) {
      mem.set(id, { media: rt.client.media, handle: rt.handle });
      if (rt.handle) sh.handles[id] = rt.handle;
    }
    for (const id of [...mem.keys()]) if (!ids.has(id)) mem.delete(id);
    for (const id of Object.keys(sh.handles) as SourceId[]) if (!ids.has(id)) delete sh.handles[id];
    this.files.set(sh.id, mem);
  }

  toDoc(wp: WsProject): ProjectDoc {
    if (this.activeProject() === wp) this.sync();
    return {
      schemaVersion: '2.0',
      appVersion: APP_VERSION,
      id: wp.id,
      name: wp.name,
      createdAt: wp.createdAt,
      updatedAt: wp.updatedAt,
      shots: wp.shots.map((s) => ({ id: s.id, project: s.project })),
      activeShotId: wp.activeShotId,
      extra: wp.extra,
    };
  }

  /** Something changed in a project (structure, or the active shot's content): refresh dirty state and persist. */
  changed(pid?: string, immediate = false): void {
    const run = () => {
      for (const wp of this.projects) if (!pid || wp.id === pid) this.dirty.set(wp.id, wp.baseline !== null && docContentKey(this.toDoc(wp)) !== wp.baseline);
      this.app.store.notify();
    };
    clearTimeout(this.dirtyTimer);
    if (immediate) run();
    else this.dirtyTimer = window.setTimeout(run, 300);
    this.schedulePersist();
    this.app.store.notify();
  }

  /** Called on every project change of the active shot. */
  onActiveEdited(): void {
    this.changed(this.active?.pid);
  }

  // ---------------------------------------------------------------------------------------- persistence

  schedulePersist(): void {
    clearTimeout(this.persistTimer);
    this.persistTimer = window.setTimeout(() => void this.persist(), 1500);
  }

  async persist(): Promise<void> {
    clearTimeout(this.persistTimer);
    this.sync();
    const rec: WsRecord = {
      v: 1,
      active: this.active,
      projects: this.projects.map((wp) => ({
        json: serializeDoc(this.toDoc(wp), APP_VERSION),
        fileHandle: wp.fileHandle,
        fileName: wp.fileName,
        baseline: wp.baseline,
        readOnly: wp.readOnly,
        expanded: wp.expanded,
        mediaDir: wp.mediaDir,
        handles: Object.fromEntries(wp.shots.map((s) => [s.id, { ...s.handles }])),
      })),
    };
    await kvSet('workspace', rec);
  }

  /** Startup: reopen the projects of the last session (the user's choice for F-18), or start an empty one. */
  async restore(): Promise<void> {
    const rec = await kvGet<WsRecord>('workspace');
    for (const r of rec?.projects ?? []) {
      const d = parseDoc(r.json);
      if (!d.ok) continue;
      const wp = this.fromDoc(d.doc, r.baseline, r.readOnly);
      wp.fileHandle = r.fileHandle;
      wp.fileName = r.fileName;
      wp.expanded = r.expanded;
      wp.mediaDir = r.mediaDir;
      for (const s of wp.shots) s.handles = { ...(r.handles?.[s.id] ?? {}) };
      this.projects.push(wp);
    }
    if (!rec) await this.importOldAutosave();
    if (!this.projects.length) this.projects.push(this.blankProject());
    for (const wp of this.projects) this.dirty.set(wp.id, wp.baseline !== null && docContentKey(this.toDoc(wp)) !== wp.baseline);
    const want = rec?.active && this.project(rec.active.pid)?.shots.some((s) => s.id === rec.active!.sid) ? rec.active : null;
    const first = this.projects[0];
    await this.activate(want?.pid ?? first.id, want?.sid ?? first.activeShotId ?? first.shots[0].id, { force: true });
  }

  /** Autosave of the single-project version (before F-18) becomes a project in the tree. */
  private async importOldAutosave(): Promise<void> {
    const old = await kvGet<AutosaveRecord>('autosave');
    if (!old) return;
    const d = parseDoc(old.projectJson, t('project.untitled'));
    if (!d.ok) return;
    const wp = this.fromDoc(d.doc, old.clean ? docContentKey(d.doc) : null, d.readOnly);
    wp.fileHandle = old.projectHandle;
    wp.fileName = old.projectHandle?.name;
    const handles: Handles = {};
    for (const [k, v] of Object.entries(old.handles ?? {})) if (v) handles[k as SourceId] = v;
    wp.shots[0].handles = handles;
    this.projects.push(wp);
  }

  private fromDoc(doc: ProjectDoc, baseline: string | null, readOnly: boolean): WsProject {
    const shots: WsShot[] = doc.shots.map((s) => ({ id: s.id, project: s.project, handles: {} }));
    if (!shots.length) shots.push(this.blankShot());
    return {
      id: doc.id,
      name: doc.name,
      createdAt: doc.createdAt,
      updatedAt: doc.updatedAt,
      extra: doc.extra,
      shots,
      activeShotId: doc.activeShotId,
      baseline,
      readOnly,
      expanded: true,
    };
  }

  private blankShot(): WsShot {
    return { id: newId('shot'), project: createProject('', APP_VERSION), handles: {} };
  }

  private blankProject(name = t('project.untitled')): WsProject {
    const d = createDoc(name, APP_VERSION);
    return { id: d.id, name, createdAt: d.createdAt, updatedAt: d.updatedAt, shots: [this.blankShot()], baseline: null, readOnly: false, expanded: true };
  }

  /** The untouched default project (replaced when a project file is opened). */
  private isPristine(wp: WsProject): boolean {
    return wp.baseline === null && !this.hasContent(wp) && wp.shots.every((s) => !this.shotProject(s).name);
  }

  // ---------------------------------------------------------------------------------------- activation

  /** Shows another shot: unloads the current videos and re-links the target's (D-41). */
  async activate(pid: string, sid: string, opts: { force?: boolean } = {}): Promise<void> {
    const wp = this.project(pid);
    const sh = wp?.shots.find((s) => s.id === sid);
    if (!wp || !sh) return;
    if (!opts.force && this.active?.pid === pid && this.active.sid === sid) return;
    const app = this.app;
    this.sync();
    this.activationSeq++;
    if (app.player.playing) app.player.pause();
    this.unloadRuntime();
    this.active = { pid, sid };
    wp.activeShotId = sid;
    wp.expanded = true;
    app.store.replaceProject(sh.project);
    app.store.ui((u) => {
      u.readOnly = wp.readOnly;
      u.tempFull = null;
      u.alignOpen = false;
      u.dirty = false;
    });
    app.sources.rebuildTimeline();
    app.sources.onChange?.();
    this.schedulePersist();
    await this.relinkActive(false);
  }

  private unloadRuntime(): void {
    const s = this.app.sources;
    s.cancelLoads();
    for (const rt of s.runtime.values()) rt.client.dispose();
    s.runtime.clear();
    s.suggestions.clear();
    s.errors.clear();
    s.estimating.clear();
    void this.app.player.syncAudioSource();
  }

  /**
   * Re-opens the active shot's videos: files already opened this session, then stored handles that still have
   * permission. With `ask` (inside a click), stored handles and the media folder may prompt for permission.
   */
  async relinkActive(ask: boolean): Promise<void> {
    const seq = this.activationSeq;
    const wp = this.activeProject();
    const sh = this.activeShot();
    if (!wp || !sh) return;
    const app = this.app;
    if (ask && wp.mediaDir) await readable(wp.mediaDir, true);
    const mem = this.files.get(sh.id);
    const todo = app.project.sources.filter((s) => !app.sources.runtime.has(s.id) && !app.sources.loading.has(s.id));
    await Promise.all(
      todo.map(async (s) => {
        const m = mem?.get(s.id);
        let media: MediaInput | null = m?.media ?? null;
        let handle = m?.handle ?? sh.handles[s.id];
        if (!media && handle) {
          if (await readable(handle, ask)) media = await this.openHandle(handle, s);
          else if (ask) toast(t('error.permission', { id: s.id, name: s.file.name }), 6000);
        }
        // A video from a link is downloaded again when no local copy can be opened (another computer, F-01.13). With
        // "save into a folder" and a folder this computer may already write to, a copy is kept there and remembered,
        // so the next opening reads the file instead of downloading again.
        let saved: FileSystemFileHandle | null = null;
        if (!media && s.remote && !this.fetching.has(s.remote.id)) {
          this.fetching.add(s.remote.id);
          app.store.notify();
          try {
            const f = await downloadRemote(s.remote);
            media = f;
            const dir = app.state.settings.linkSave === 'none' ? null : ((await app.projectFolder(false)) ?? (await app.linkFolder(false)));
            if (dir) saved = await saveIntoFolder(dir, f);
          } catch {
            media = null;
          } finally {
            this.fetching.delete(s.remote.id);
            app.store.notify();
          }
        }
        if (!media || seq !== this.activationSeq) return;
        if (!m) handle = saved ?? sh.handles[s.id];
        await this.linkFile(s.id, media, handle, seq);
      }),
    );
    if (seq !== this.activationSeq) return;
    void app.player.syncAudioSource();
    app.player.seek(app.project.playback.tick);
    app.scheduleSuggestions();
    this.changed(wp.id);
  }

  /** Attaches a file to a slot of the active shot that is listed in the project, checking the fingerprint. */
  async linkFile(id: SourceId, media: MediaInput, handle?: FileSystemHandle, seq = this.activationSeq): Promise<boolean> {
    const app = this.app;
    if (seq !== this.activationSeq) return false;
    const file = { name: media instanceof Blob ? media.name : media.pattern };
    const r = await app.sources.relink(id, media, handle);
    if (seq !== this.activationSeq) return false;
    if (!r.ok) {
      const e = app.sources.errors.get(id);
      toast(t('toast.loadFailed', { name: file.name }) + (e ? ` — ${t(e.errorKey)}` : ''), 5000);
      return false;
    }
    if (!r.fingerprintMatch) toast(t('toast.fingerprint', { id, name: file.name }), 6000);
    const sh = this.activeShot();
    if (sh) {
      const mem = this.files.get(sh.id) ?? new Map();
      mem.set(id, { media, handle });
      this.files.set(sh.id, mem);
      if (handle) sh.handles[id] = handle;
    }
    return true;
  }

  /** Opens a stored handle: the video file, or the sequence with the source's pattern inside the folder. */
  private async openHandle(h: FileSystemHandle, s: Source): Promise<MediaInput | null> {
    try {
      if (h.kind === 'file') return await (h as FileSystemFileHandle).getFile();
      const seq = await sequenceFromDirectory(h as FileSystemDirectoryHandle, s.spec.frameRate, s.sequence?.pattern);
      return seq ? withSourceSettings(seq, s) : null;
    } catch {
      return null;
    }
  }

  /** Asks for permission on the stored handles of the active shot (button in the relink card). */
  async allowStored(): Promise<void> {
    await this.relinkActive(true);
  }

  /**
   * Media folder (F-18.6): finds every missing video of every shot of the project in a folder (by size, then
   * fingerprint) and remembers the handles; the active shot's videos open right away.
   */
  async pickMediaFolder(pid: string): Promise<void> {
    const wp = this.project(pid);
    if (!wp) return;
    let dir: FileSystemDirectoryHandle | null;
    try {
      dir = await fsAccess.pickDirectory('read');
    } catch (e) {
      if ((e as DOMException).name !== 'AbortError') toast(t('toast.folderFailed', { detail: String(e) }), 5000);
      return;
    }
    if (!dir) {
      toast(t('toast.folderUnsupported'), 5000);
      return;
    }
    wp.mediaDir = dir;
    this.scanning = true;
    this.app.store.notify();
    try {
      const found = await scanFolder(dir);
      const fps = new Map<FoundMedia | FoundSequence, string>();
      const fp = async (c: FoundMedia | FoundSequence) => {
        if (!fps.has(c)) fps.set(c, 'seq' in c ? (await sequenceIdentity(c.seq)).fingerprint : await fingerprintFile(c.file));
        return fps.get(c)!;
      };
      let files = 0;
      const shots = new Set<string>();
      this.sync();
      for (const sh of wp.shots) {
        const isActive = this.active?.sid === sh.id;
        const mem = this.files.get(sh.id);
        for (const s of this.shotProject(sh).sources) {
          if (isActive ? this.app.sources.runtime.has(s.id) : mem?.has(s.id)) continue;
          if (s.sequence) {
            const want = s.sequence;
            for (const c of found.sequences) {
              if (c.seq.kind !== want.kind || c.seq.files.length !== want.count) continue;
              if ((await fp(c)) !== s.file.fingerprint) continue;
              sh.handles[s.id] = c.dir;
              files++;
              shots.add(sh.id);
              if (isActive) await this.linkFile(s.id, withSourceSettings(c.seq, s), c.dir);
              break;
            }
            continue;
          }
          for (const c of rankCandidates({ name: s.file.name, size: s.file.size }, found.videos)) {
            if ((await fp(c)) !== s.file.fingerprint) continue;
            sh.handles[s.id] = c.handle;
            files++;
            shots.add(sh.id);
            if (isActive) await this.linkFile(s.id, c.file, c.handle);
            break;
          }
        }
      }
      toast(files ? t('toast.folderMatched', { n: files, shots: shots.size }) : t('toast.folderNone'), 4000);
      if (this.active?.pid === pid) {
        void this.app.player.syncAudioSource();
        this.app.player.seek(this.app.project.playback.tick);
      }
    } finally {
      this.scanning = false;
      this.changed(pid);
    }
  }

  // ---------------------------------------------------------------------------------------- structure

  /** A new project; `folder` is its default folder (F-18.10: downloads, exports and screenshots go there). */
  async newProject(name = t('project.untitled'), folder?: FileSystemDirectoryHandle): Promise<WsProject> {
    const wp = this.blankProject(name);
    if (folder) wp.mediaDir = folder;
    this.projects.unshift(wp);
    this.changed(wp.id, true);
    await this.activate(wp.id, wp.shots[0].id);
    return wp;
  }

  async addShot(pid: string, name = ''): Promise<WsShot | null> {
    const wp = this.project(pid);
    if (!wp) return null;
    const sh = this.blankShot();
    sh.project.name = name;
    wp.shots.push(sh);
    wp.expanded = true;
    this.changed(pid, true);
    await this.activate(pid, sh.id);
    return sh;
  }

  renameProject(pid: string, name: string): void {
    const wp = this.project(pid);
    if (!wp || !name.trim()) return;
    wp.name = name.trim();
    this.changed(pid, true);
  }

  renameShot(pid: string, sid: string, name: string): void {
    const wp = this.project(pid);
    const sh = wp?.shots.find((s) => s.id === sid);
    if (!wp || !sh || !name.trim()) return;
    if (this.active?.sid === sid) this.app.store.project((p) => (p.name = name.trim()), { undo: false });
    else sh.project.name = name.trim();
    this.changed(pid, true);
  }

  private cloneShot(sh: WsShot, rename: boolean): WsShot {
    const p = structuredClone(this.shotProject(sh));
    if (rename) p.name = (p.name || t('tree.shot')) + t('tree.copySuffix');
    const copy: WsShot = { id: newId('shot'), project: p, handles: { ...sh.handles } };
    const mem = this.files.get(sh.id);
    if (mem) this.files.set(copy.id, new Map(mem));
    return copy;
  }

  duplicateShot(pid: string, sid: string): void {
    const wp = this.project(pid);
    const i = wp?.shots.findIndex((s) => s.id === sid) ?? -1;
    if (!wp || i < 0) return;
    this.sync();
    wp.shots.splice(i + 1, 0, this.cloneShot(wp.shots[i], true));
    this.changed(pid, true);
  }

  duplicateProject(pid: string): void {
    const wp = this.project(pid);
    const i = this.projects.indexOf(wp!);
    if (!wp) return;
    this.sync();
    const d = createDoc(wp.name + t('tree.copySuffix'), APP_VERSION);
    const copy: WsProject = {
      id: d.id,
      name: d.name,
      createdAt: d.createdAt,
      updatedAt: d.updatedAt,
      extra: wp.extra ? structuredClone(wp.extra) : undefined,
      shots: wp.shots.map((s) => this.cloneShot(s, false)),
      baseline: null,
      readOnly: false,
      expanded: true,
      mediaDir: wp.mediaDir,
    };
    this.projects.splice(i + 1, 0, copy);
    this.changed(copy.id, true);
  }

  canMoveShot(pid: string): boolean {
    return (this.project(pid)?.shots.length ?? 0) > 1 && this.projects.length > 1;
  }

  moveShot(pid: string, sid: string, toPid: string): void {
    const from = this.project(pid);
    const to = this.project(toPid);
    const i = from?.shots.findIndex((s) => s.id === sid) ?? -1;
    if (!from || !to || from === to || i < 0 || from.shots.length < 2) return;
    this.sync();
    const [sh] = from.shots.splice(i, 1);
    to.shots.push(sh);
    to.expanded = true;
    if (this.active?.sid === sid) {
      this.active = { pid: toPid, sid };
      to.activeShotId = sid;
      from.activeShotId = from.shots[0].id;
      this.app.store.ui((u) => (u.readOnly = to.readOnly));
    }
    this.changed(undefined, true);
  }

  moveUpDown(pid: string, sid: string | null, dir: -1 | 1): void {
    const wp = this.project(pid);
    if (!wp) return;
    const list: { id: string }[] = sid ? wp.shots : this.projects;
    const i = list.findIndex((x) => x.id === (sid ?? pid));
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    this.changed(sid ? pid : undefined, true);
  }

  async deleteShot(pid: string, sid: string): Promise<void> {
    const wp = this.project(pid);
    const i = wp?.shots.findIndex((s) => s.id === sid) ?? -1;
    if (!wp || i < 0 || wp.shots.length < 2) return;
    const wasActive = this.active?.sid === sid;
    if (wasActive) {
      const next = wp.shots[i + 1] ?? wp.shots[i - 1];
      await this.activate(pid, next.id);
    }
    wp.shots.splice(i, 1);
    this.files.delete(sid);
    if (wp.activeShotId === sid) wp.activeShotId = wp.shots[0].id;
    this.changed(pid, true);
  }

  /** Removes a project from the tree (the file stays). The last project is replaced by a new empty one. */
  async closeProject(pid: string): Promise<void> {
    const i = this.projects.findIndex((p) => p.id === pid);
    if (i < 0) return;
    const wasActive = this.active?.pid === pid;
    const [wp] = this.projects.splice(i, 1);
    for (const s of wp.shots) this.files.delete(s.id);
    this.dirty.delete(pid);
    if (!this.projects.length) this.projects.push(this.blankProject());
    if (wasActive) {
      this.active = null;
      const next = this.projects[Math.min(i, this.projects.length - 1)];
      await this.activate(next.id, next.activeShotId ?? next.shots[0].id, { force: true });
    }
    this.changed(undefined, true);
  }

  /** Clears the stored videos of a shot so they can be chosen again. */
  forgetVideos(pid: string, sid: string): void {
    const sh = this.project(pid)?.shots.find((s) => s.id === sid);
    if (!sh) return;
    sh.handles = {};
    this.files.delete(sid);
    if (this.active?.sid === sid) {
      this.unloadRuntime();
      this.app.sources.rebuildTimeline();
      this.app.sources.onChange?.();
    }
    this.changed(pid);
  }

  // ---------------------------------------------------------------------------------------- files

  async openFile(file: File, handle?: FileSystemFileHandle): Promise<void> {
    await this.openText(await file.text(), file.name, handle);
  }

  async openText(text: string, fileName: string, handle?: FileSystemFileHandle): Promise<void> {
    const r = parseDoc(text, fileName.replace(/\.[^.]+$/, ''));
    if (!r.ok) {
      toast(t(r.errorKey), 5000);
      return;
    }
    // The same file is already in the tree: show it instead of opening a second copy.
    for (const wp of this.projects) {
      const same = handle && wp.fileHandle ? await wp.fileHandle.isSameEntry(handle).catch(() => false) : false;
      if (same) {
        toast(t('toast.projectAlreadyOpen', { name: wp.name }));
        await this.activate(wp.id, wp.activeShotId ?? wp.shots[0].id);
        return;
      }
    }
    // A second copy of an open project (Save As copy, same file opened without a handle) gets its own ids in the
    // tree; the ids are only identities inside the workspace.
    const usedShots = new Set(this.projects.flatMap((p) => p.shots.map((s) => s.id)));
    if (this.project(r.doc.id)) r.doc.id = newId('proj');
    for (const s of r.doc.shots) {
      if (!usedShots.has(s.id)) continue;
      const fresh = newId('shot');
      if (r.doc.activeShotId === s.id) r.doc.activeShotId = fresh;
      s.id = fresh;
    }
    const wp = this.fromDoc(r.doc, r.readOnly ? null : docContentKey(r.doc), r.readOnly);
    wp.fileHandle = handle;
    wp.fileName = handle?.name ?? fileName;
    const pristine = this.projects.length === 1 && this.isPristine(this.projects[0]) ? this.projects[0] : null;
    this.projects.unshift(wp);
    this.dirty.set(wp.id, false);
    if (r.readOnly) toast(t('project.warn.newer'), 6000);
    else toast(t('toast.opened', { name: wp.fileName }));
    await this.activate(wp.id, wp.activeShotId ?? wp.shots[0].id);
    if (pristine) await this.closeProject(pristine.id);
    this.changed(wp.id, true);
  }

  fileNameFor(wp: WsProject): string {
    return wp.fileName ?? `${(wp.name || t('project.untitled')).replace(/[\\/:*?"<>|]+/g, '_')}${PROJECT_EXT}`;
  }

  async save(pid: string, saveAs = false): Promise<boolean> {
    const wp = this.project(pid);
    if (!wp) return false;
    if (wp.readOnly) {
      toast(t('toast.readOnly'), 4000);
      saveAs = true;
    }
    const doc = this.toDoc(wp);
    wp.updatedAt = new Date().toISOString();
    const json = serializeDoc(doc, APP_VERSION);
    try {
      let name: string;
      if (fsAccess.supported) {
        let h = saveAs ? null : (wp.fileHandle ?? null);
        if (h && !(await ensureWritable(h))) h = null;
        if (!h) h = await fsAccess.saveAs(this.fileNameFor(wp), 'AiComp project', PROJECT_EXT, 'application/json');
        if (!h) return false;
        await writeFile(h, json);
        wp.fileHandle = h;
        name = h.name;
      } else {
        name = this.fileNameFor(wp);
        downloadBlob(new Blob([json], { type: 'application/json' }), name);
      }
      wp.fileName = name;
      wp.readOnly = false;
      wp.baseline = docContentKey(doc);
      if (this.active?.pid === pid) this.app.store.ui((u) => (u.readOnly = false));
      toast(t('toast.savedTo', { name }));
      this.changed(pid, true);
      await this.persist();
      return true;
    } catch (e) {
      if ((e as DOMException).name === 'AbortError') return false;
      toast(t('error.save', { detail: String(e) }), 6000);
      return false;
    }
  }
}
